import React, { useEffect, useState, useRef } from 'react';
import { ChevronRightIcon, ShoppingCartIcon, CalendarIcon, UserIcon, FileTextIcon, ClipboardCheckIcon, CheckCircleIcon, TruckIcon, ArrowLeftIcon, SaveIcon, PackageIcon, SearchIcon, UploadIcon, CameraIcon, XIcon, AlertCircleIcon, FileIcon, ImageIcon, PaperclipIcon } from 'lucide-react';
import { ReceiptHeader } from './ReceiptHeader';
import { ReceiptLineCard } from './ReceiptLineCard';
import { ReceiptSummary } from './ReceiptSummary';
import { StatusBadge } from '../StatusBadge';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDate } from '../utils/dateFormatter';
// Define types for the component
export type ReceiptMode = 'Quantity' | 'Percentage' | 'Value';
export type LineType = 'Item' | 'Service';
export type ReceiptStatus = 'Draft' | 'Submitted' | 'Cancelled';
export type DeliveryDocket = {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  uploadDate: string;
};
export type ReceiptHistoryItem = {
  id: string;
  date: string;
  mode: ReceiptMode;
  amountReceived: number;
  valueReceived: number;
  percentageReceived: number;
  enteredBy: string;
  status: ReceiptStatus;
  notes?: string;
};
export type PurchaseOrderLine = {
  id: string;
  itemCode: string;
  description: string;
  type: LineType;
  quantity: number;
  uom: string;
  unitPrice: number;
  totalPrice: number;
  receivedToDate: number;
  receivedValueToDate: number;
  receiptHistory: ReceiptHistoryItem[];
};
export type PurchaseOrder = {
  id: string;
  name: string;
  vendor: string;
  status: string;
  date: string;
  entity: string;
  site: string;
  currency: string;
  total: number;
  lines: PurchaseOrderLine[];
  requiresDeliveryDocket?: boolean;
};
interface ReceiptingViewProps {
  poId?: string;
  initialPO?: any;
  locale?: string;
  onBack?: () => void;
}
export function ReceiptingView({
  poId,
  initialPO,
  locale = 'en-US',
  onBack
}: ReceiptingViewProps) {
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [receiptDate, setReceiptDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [receiptedBy, setReceiptedBy] = useState<string>('Admin User');
  const [receiptEntries, setReceiptEntries] = useState<{
    [key: string]: {
      mode: ReceiptMode;
      amount: number;
      notes: string;
    };
  }>({});
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const [receiptStatus, setReceiptStatus] = useState<ReceiptStatus>('Draft');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<PurchaseOrder[]>([]);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [deliveryDockets, setDeliveryDockets] = useState<DeliveryDocket[]>([]);
  const [showCameraModal, setShowCameraModal] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasCamera, setHasCamera] = useState<boolean>(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  // Sample PO data for search
  const samplePOs: PurchaseOrder[] = [{
    id: 'PO-2023-001',
    name: 'IT Equipment and Support Services',
    vendor: 'Tech Solutions Inc.',
    status: 'Approved',
    date: '2023-11-01',
    entity: 'Corporate',
    site: 'Headquarters',
    currency: 'USD',
    total: 15750.0,
    requiresDeliveryDocket: true,
    lines: [{
      id: 'LINE-001',
      itemCode: 'LPT-001',
      description: 'Dell XPS 15 Laptop',
      type: 'Item',
      quantity: 5,
      uom: 'EA',
      unitPrice: 1500.0,
      totalPrice: 7500.0,
      receivedToDate: 2,
      receivedValueToDate: 3000.0,
      receiptHistory: [{
        id: 'REC-001',
        date: '2023-11-05',
        mode: 'Quantity',
        amountReceived: 2,
        valueReceived: 3000.0,
        percentageReceived: 40,
        enteredBy: 'John Smith',
        status: 'Submitted',
        notes: 'Initial delivery of laptops'
      }]
    }, {
      id: 'LINE-002',
      itemCode: 'MON-002',
      description: 'Dell 27" 4K Monitor',
      type: 'Item',
      quantity: 10,
      uom: 'EA',
      unitPrice: 350.0,
      totalPrice: 3500.0,
      receivedToDate: 0,
      receivedValueToDate: 0,
      receiptHistory: []
    }, {
      id: 'LINE-003',
      itemCode: 'SVC-001',
      description: 'IT Setup and Configuration Services',
      type: 'Service',
      quantity: 1,
      uom: 'LOT',
      unitPrice: 2500.0,
      totalPrice: 2500.0,
      receivedToDate: 0.5,
      receivedValueToDate: 1250.0,
      receiptHistory: [{
        id: 'REC-002',
        date: '2023-11-10',
        mode: 'Percentage',
        amountReceived: 0.5,
        valueReceived: 1250.0,
        percentageReceived: 50,
        enteredBy: 'Jane Doe',
        status: 'Submitted',
        notes: 'Initial setup completed'
      }]
    }, {
      id: 'LINE-004',
      itemCode: 'SVC-002',
      description: 'Annual Support Contract',
      type: 'Service',
      quantity: 1,
      uom: 'YR',
      unitPrice: 2250.0,
      totalPrice: 2250.0,
      receivedToDate: 0,
      receivedValueToDate: 0,
      receiptHistory: []
    }]
  }, {
    id: 'PO-2023-002',
    name: 'Office Supplies',
    vendor: 'Office Depot',
    status: 'Approved',
    date: '2023-10-15',
    entity: 'Corporate',
    site: 'Headquarters',
    currency: 'USD',
    total: 1250.75,
    requiresDeliveryDocket: false,
    lines: [{
      id: 'LINE-001',
      itemCode: 'PAP-001',
      description: 'Printer Paper',
      type: 'Item',
      quantity: 50,
      uom: 'BOX',
      unitPrice: 10.0,
      totalPrice: 500.0,
      receivedToDate: 0,
      receivedValueToDate: 0,
      receiptHistory: []
    }, {
      id: 'LINE-002',
      itemCode: 'PEN-001',
      description: 'Ballpoint Pens',
      type: 'Item',
      quantity: 100,
      uom: 'BOX',
      unitPrice: 2.0,
      totalPrice: 200.0,
      receivedToDate: 0,
      receivedValueToDate: 0,
      receiptHistory: []
    }]
  }, {
    id: 'PO-2023-003',
    name: 'Marketing Materials',
    vendor: 'PrintWorks Inc.',
    status: 'Approved',
    date: '2023-10-05',
    entity: 'Marketing',
    site: 'Headquarters',
    currency: 'USD',
    total: 3200.5,
    requiresDeliveryDocket: true,
    lines: [{
      id: 'LINE-001',
      itemCode: 'BRO-001',
      description: 'Company Brochures',
      type: 'Item',
      quantity: 1000,
      uom: 'EA',
      unitPrice: 1.5,
      totalPrice: 1500.0,
      receivedToDate: 500,
      receivedValueToDate: 750.0,
      receiptHistory: [{
        id: 'REC-001',
        date: '2023-10-10',
        mode: 'Quantity',
        amountReceived: 500,
        valueReceived: 750.0,
        percentageReceived: 50,
        enteredBy: 'Mark Johnson',
        status: 'Submitted',
        notes: 'Initial delivery of brochures'
      }]
    }]
  }];
  // Check if camera is available
  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setHasCamera(true);
    }
  }, []);
  // Cleanup camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);
  // Fetch PO data (simulated)
  useEffect(() => {
    // This would be an API call in a real application
    const fetchPO = async () => {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      // First check if we have an initialPO passed directly
      if (initialPO) {
        // Convert the initialPO to the expected PurchaseOrder format
        // This is a simplified example - in a real app you'd need proper mapping
        const formattedPO: PurchaseOrder = {
          id: initialPO.id,
          name: initialPO.name,
          vendor: initialPO.vendor,
          status: initialPO.status,
          date: initialPO.date,
          entity: 'Corporate',
          site: 'Headquarters',
          currency: 'USD',
          total: initialPO.total,
          requiresDeliveryDocket: Math.random() > 0.5,
          lines: [
          // Create a sample line for demonstration
          {
            id: 'LINE-001',
            itemCode: 'ITEM-001',
            description: `Items from ${initialPO.name}`,
            type: 'Item',
            quantity: 10,
            uom: 'EA',
            unitPrice: initialPO.total / 10,
            totalPrice: initialPO.total,
            receivedToDate: 0,
            receivedValueToDate: 0,
            receiptHistory: []
          }]
        };
        setPurchaseOrder(formattedPO);
        // Initialize receipt entries with empty values
        const initialEntries: {
          [key: string]: {
            mode: ReceiptMode;
            amount: number;
            notes: string;
          };
        } = {};
        formattedPO.lines.forEach(line => {
          initialEntries[line.id] = {
            mode: line.type === 'Item' ? 'Quantity' : 'Percentage',
            amount: 0,
            notes: ''
          };
        });
        setReceiptEntries(initialEntries);
      } else if (poId) {
        // Find the PO in our sample data
        const foundPO = samplePOs.find(po => po.id === poId);
        if (foundPO) {
          setPurchaseOrder(foundPO);
          // Initialize receipt entries with empty values
          const initialEntries: {
            [key: string]: {
              mode: ReceiptMode;
              amount: number;
              notes: string;
            };
          } = {};
          foundPO.lines.forEach(line => {
            initialEntries[line.id] = {
              mode: line.type === 'Item' ? 'Quantity' : 'Percentage',
              amount: 0,
              notes: ''
            };
          });
          setReceiptEntries(initialEntries);
        }
      } else if (samplePOs.length > 0) {
        // Default to the first PO if no specific one is requested
        setPurchaseOrder(samplePOs[0]);
        // Initialize receipt entries with empty values
        const initialEntries: {
          [key: string]: {
            mode: ReceiptMode;
            amount: number;
            notes: string;
          };
        } = {};
        samplePOs[0].lines.forEach(line => {
          initialEntries[line.id] = {
            mode: line.type === 'Item' ? 'Quantity' : 'Percentage',
            amount: 0,
            notes: ''
          };
        });
        setReceiptEntries(initialEntries);
      }
      setLoading(false);
    };
    fetchPO();
  }, [poId, initialPO]);
  // Handle file input change
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      // Process each file
      files.forEach(file => {
        // Create a URL for the file
        const fileUrl = URL.createObjectURL(file);
        // Add to delivery dockets
        const newDocket: DeliveryDocket = {
          id: `docket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          type: file.type,
          size: file.size,
          url: fileUrl,
          uploadDate: new Date().toISOString()
        };
        setDeliveryDockets(prev => [...prev, newDocket]);
      });
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  // Handle camera capture
  const handleStartCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCameraModal(true);
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Could not access camera. Please check your permissions.');
    }
  };
  const handleCaptureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      // Draw video frame to canvas
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        // Convert canvas to blob
        canvas.toBlob(blob => {
          if (blob) {
            const fileUrl = URL.createObjectURL(blob);
            // Create a filename with timestamp
            const fileName = `delivery-docket-${new Date().toISOString().replace(/:/g, '-')}.jpg`;
            // Add to delivery dockets
            const newDocket: DeliveryDocket = {
              id: `docket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              name: fileName,
              type: 'image/jpeg',
              size: blob.size,
              url: fileUrl,
              uploadDate: new Date().toISOString()
            };
            setDeliveryDockets(prev => [...prev, newDocket]);
          }
        }, 'image/jpeg', 0.8);
      }
    }
  };
  const handleCloseCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
  };
  // Remove a delivery docket
  const handleRemoveDocket = (id: string) => {
    setDeliveryDockets(prev => prev.filter(docket => docket.id !== id));
  };
  // Handle search
  const handleSearch = () => {
    setIsSearching(true);
    // Simulate API delay
    setTimeout(() => {
      // If search query is empty, show all POs
      if (!searchQuery.trim()) {
        setSearchResults(samplePOs);
      } else {
        const results = samplePOs.filter(po => po.id.toLowerCase().includes(searchQuery.toLowerCase()) || po.name.toLowerCase().includes(searchQuery.toLowerCase()) || po.vendor.toLowerCase().includes(searchQuery.toLowerCase()));
        setSearchResults(results);
      }
      setShowSearchResults(true);
      setIsSearching(false);
    }, 500);
  };
  // New function to view all POs
  const handleViewAll = () => {
    setSearchQuery('');
    setIsSearching(true);
    // Simulate API delay
    setTimeout(() => {
      setSearchResults(samplePOs);
      setShowSearchResults(true);
      setIsSearching(false);
    }, 500);
  };
  // Handle selecting a PO from search results
  const handleSelectPO = (po: PurchaseOrder) => {
    setPurchaseOrder(po);
    // Initialize receipt entries with empty values
    const initialEntries: {
      [key: string]: {
        mode: ReceiptMode;
        amount: number;
        notes: string;
      };
    } = {};
    po.lines.forEach(line => {
      initialEntries[line.id] = {
        mode: line.type === 'Item' ? 'Quantity' : 'Percentage',
        amount: 0,
        notes: ''
      };
    });
    setReceiptEntries(initialEntries);
    setSearchQuery('');
    setShowSearchResults(false);
    // Reset any errors
    setErrors({});
    // Clear any existing delivery dockets
    setDeliveryDockets([]);
  };
  // Handle receipt mode change
  const handleModeChange = (lineId: string, mode: ReceiptMode) => {
    setReceiptEntries(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        mode,
        amount: 0 // Reset amount when changing mode
      }
    }));
    // Clear any errors for this line
    if (errors[lineId]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[lineId];
        return newErrors;
      });
    }
  };
  // Handle receipt amount change
  const handleAmountChange = (lineId: string, amount: number) => {
    setReceiptEntries(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        amount
      }
    }));
    // Validate the input
    validateReceiptAmount(lineId, amount);
  };
  // Handle notes change
  const handleNotesChange = (lineId: string, notes: string) => {
    setReceiptEntries(prev => ({
      ...prev,
      [lineId]: {
        ...prev[lineId],
        notes
      }
    }));
  };
  // Validate receipt amount
  const validateReceiptAmount = (lineId: string, amount: number) => {
    if (!purchaseOrder) return;
    const line = purchaseOrder.lines.find(l => l.id === lineId);
    if (!line) return;
    const entry = receiptEntries[lineId];
    if (!entry) return;
    let error = '';
    if (amount < 0) {
      error = 'Amount cannot be negative';
    } else if (entry.mode === 'Quantity') {
      const remaining = line.quantity - line.receivedToDate;
      if (amount > remaining) {
        error = `Cannot receive more than the remaining quantity (${remaining})`;
      }
    } else if (entry.mode === 'Percentage') {
      const percentageReceived = line.receivedToDate / line.quantity * 100;
      const remainingPercentage = 100 - percentageReceived;
      if (amount > remainingPercentage) {
        error = `Cannot receive more than the remaining percentage (${remainingPercentage.toFixed(2)}%)`;
      }
    } else if (entry.mode === 'Value') {
      const remainingValue = line.totalPrice - line.receivedValueToDate;
      if (amount > remainingValue) {
        error = `Cannot receive more than the remaining value (${formatCurrency(remainingValue, purchaseOrder.currency, locale)})`;
      }
    }
    if (error) {
      setErrors(prev => ({
        ...prev,
        [lineId]: error
      }));
    } else {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[lineId];
        return newErrors;
      });
    }
  };
  // Calculate receipt totals
  const calculateTotals = () => {
    if (!purchaseOrder) return {
      totalThisReceipt: 0,
      totalReceived: 0,
      totalRemaining: 0
    };
    let totalThisReceipt = 0;
    let totalReceived = 0;
    let totalRemaining = 0;
    purchaseOrder.lines.forEach(line => {
      const entry = receiptEntries[line.id];
      if (entry && entry.amount > 0) {
        let valueThisReceipt = 0;
        if (entry.mode === 'Quantity') {
          valueThisReceipt = entry.amount * line.unitPrice;
        } else if (entry.mode === 'Percentage') {
          valueThisReceipt = entry.amount / 100 * line.totalPrice;
        } else {
          // Value
          valueThisReceipt = entry.amount;
        }
        totalThisReceipt += valueThisReceipt;
      }
      totalReceived += line.receivedValueToDate;
      totalRemaining += line.totalPrice - line.receivedValueToDate;
    });
    return {
      totalThisReceipt,
      totalReceived,
      totalRemaining
    };
  };
  // Handle save as draft
  const handleSaveAsDraft = () => {
    setReceiptStatus('Draft');
    saveReceipt();
  };
  // Handle submit
  const handleSubmit = () => {
    // Validate all entries before submission
    const newErrors: {
      [key: string]: string;
    } = {};
    let hasErrors = false;
    // Check if delivery docket is required but missing
    if (purchaseOrder?.requiresDeliveryDocket && deliveryDockets.length === 0) {
      newErrors['deliveryDocket'] = 'Delivery docket is required for this purchase order';
      hasErrors = true;
    }
    purchaseOrder?.lines.forEach(line => {
      const entry = receiptEntries[line.id];
      // Check if any line is receiving its final quantity but has no notes
      const isReceivingFinal = entry.mode === 'Quantity' && entry.amount + line.receivedToDate === line.quantity;
      if (isReceivingFinal && !entry.notes) {
        newErrors[line.id] = 'Notes are required for final receipt';
        hasErrors = true;
      }
      // Check if the current error still applies
      if (errors[line.id]) {
        newErrors[line.id] = errors[line.id];
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }
    setReceiptStatus('Submitted');
    saveReceipt();
  };
  // Save receipt (simulated)
  const saveReceipt = () => {
    // This would be an API call in a real application
    console.log('Saving receipt with status:', receiptStatus);
    console.log('Receipt date:', receiptDate);
    console.log('Receipted by:', receiptedBy);
    console.log('Receipt entries:', receiptEntries);
    console.log('Delivery dockets:', deliveryDockets);
    // Show success message
    alert(`Receipt ${receiptStatus === 'Draft' ? 'saved as draft' : 'submitted'} successfully!`);
    // In a real application, you would redirect or refresh data here
    if (onBack) {
      onBack();
    }
  };
  // Check if the form has any data entered
  const hasEnteredData = () => {
    return Object.values(receiptEntries).some(entry => entry.amount > 0 || entry.notes.trim() !== '');
  };
  // Check if the form is valid for submission
  const isFormValid = () => {
    if (purchaseOrder?.requiresDeliveryDocket && deliveryDockets.length === 0) {
      return false;
    }
    return hasEnteredData() && Object.keys(errors).length === 0;
  };
  // Get file icon based on file type
  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon className="h-5 w-5 text-blue-500" data-id="element-14377" />;
    } else if (fileType.includes('pdf')) {
      return <FileTextIcon className="h-5 w-5 text-red-500" data-id="element-14378" />;
    } else {
      return <FileIcon className="h-5 w-5 text-gray-500" data-id="element-14379" />;
    }
  };
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-14380">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" data-id="element-14381"></div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-14382">
      <div className="flex justify-between items-center" data-id="element-14383">
        <div data-id="element-14384">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-14385">Goods Receipt</h2>
          <nav className="flex mt-1" aria-label="Breadcrumb" data-id="element-14386">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-14387">
              <li className="inline-flex items-center" data-id="element-14388">
                <button onClick={onBack} className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800" data-id="element-14389">
                  <ShoppingCartIcon className="w-4 h-4 mr-2" data-id="element-14390" />
                  Procurement
                </button>
              </li>
              <li data-id="element-14391">
                <div className="flex items-center" data-id="element-14392">
                  <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-14393" />
                  <button onClick={onBack} className="ml-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 md:ml-2" data-id="element-14394">
                    Purchase Orders
                  </button>
                </div>
              </li>
              <li data-id="element-14395">
                <div className="flex items-center" data-id="element-14396">
                  <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-14397" />
                  <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2" data-id="element-14398">
                    Goods Receipt
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
        <div className="flex items-center space-x-2" data-id="element-14399">
          <StatusBadge status={receiptStatus === 'Draft' ? 'pending' : receiptStatus === 'Submitted' ? 'success' : 'error'} text={receiptStatus} size="md" data-id="element-14400" />
        </div>
      </div>

      {/* PO Search Section */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14401">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14402">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14403">
            <SearchIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14404" />
            Search Purchase Order
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-14405">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-14406">
            <div className="flex-grow" data-id="element-14407">
              <div className="relative rounded-md shadow-sm" data-id="element-14408">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-14409">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-14410" />
                </div>
                <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search by PO number, name or vendor" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }} data-id="element-14411" />
              </div>
            </div>
            <div className="flex space-x-2" data-id="element-14412">
              <button type="button" onClick={handleSearch} disabled={isSearching} className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isSearching ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-14413">
                {isSearching ? <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" data-id="element-14414"></div>
                    Searching...
                  </> : <>
                    <SearchIcon className="h-4 w-4 mr-2" data-id="element-14415" />
                    Search
                  </>}
              </button>
              <button type="button" onClick={handleViewAll} disabled={isSearching} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14416">
                View All
              </button>
            </div>
          </div>
          {/* Search Results */}
          {showSearchResults && <div className="mt-4" data-id="element-14417">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-14418">
                {searchResults.length === 0 ? 'No purchase orders found' : `Found ${searchResults.length} purchase order(s)`}
              </h4>
              {searchResults.length > 0 && <div className="overflow-x-auto border border-gray-200 rounded-md" data-id="element-14419">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-14420">
                    <thead className="bg-gray-50" data-id="element-14421">
                      <tr data-id="element-14422">
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14423">
                          PO Number
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14424">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14425">
                          Vendor
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14426">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14427">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14428">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" data-id="element-14429">
                      {searchResults.map(po => <tr key={po.id} className="hover:bg-gray-50" data-id="element-14430">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-14431">
                            {po.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14432">
                            {po.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14433">
                            {po.vendor}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14434">
                            {po.date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14435">
                            {formatCurrency(po.total, po.currency, locale)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-14436">
                            <button onClick={() => handleSelectPO(po)} className="text-indigo-600 hover:text-indigo-900" data-id="element-14437">
                              Select
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>}
        </div>
      </div>

      {purchaseOrder ? <>
          {/* PO Header */}
          <ReceiptHeader purchaseOrder={purchaseOrder} locale={locale} data-id="element-14438" />
          {/* Receipt Details */}
          <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14439">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14440">
              <div className="flex flex-wrap items-center justify-between" data-id="element-14441">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14442">
                  <PackageIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14443" />
                  Receipt Details
                </h3>
                <div className="flex flex-wrap mt-2 sm:mt-0 gap-4 w-full sm:w-auto" data-id="element-14444">
                  <div className="flex items-center w-full sm:w-auto" data-id="element-14445">
                    <label htmlFor="receipt-date" className="block text-sm font-medium text-gray-700 mr-2" data-id="element-14446">
                      Receipt Date:
                    </label>
                    <div className="relative rounded-md shadow-sm flex-grow sm:flex-grow-0" data-id="element-14447">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-14448">
                        <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-14449" />
                      </div>
                      <input type="date" id="receipt-date" value={receiptDate} onChange={e => setReceiptDate(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" data-id="element-14450" />
                    </div>
                  </div>
                  <div className="flex items-center w-full sm:w-auto" data-id="element-14451">
                    <label htmlFor="receipted-by" className="block text-sm font-medium text-gray-700 mr-2" data-id="element-14452">
                      Receipted By:
                    </label>
                    <div className="relative rounded-md shadow-sm flex-grow sm:flex-grow-0" data-id="element-14453">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-14454">
                        <UserIcon className="h-4 w-4 text-gray-400" data-id="element-14455" />
                      </div>
                      <input type="text" id="receipted-by" value={receiptedBy} onChange={e => setReceiptedBy(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" data-id="element-14456" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-4 py-5 sm:p-6 space-y-6" data-id="element-14457">
              <div className="space-y-4" data-id="element-14458">
                {purchaseOrder.lines.map(line => <ReceiptLineCard key={line.id} line={line} currency={purchaseOrder.currency} receiptEntry={receiptEntries[line.id]} error={errors[line.id]} onModeChange={mode => handleModeChange(line.id, mode)} onAmountChange={amount => handleAmountChange(line.id, amount)} onNotesChange={notes => handleNotesChange(line.id, notes)} locale={locale} data-id="element-14459" />)}
              </div>
            </div>
          </div>
          {/* Delivery Docket Section */}
          <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-14460">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-14461">
              <div className="flex items-center justify-between" data-id="element-14462">
                <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14463">
                  <FileTextIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14464" />
                  Delivery Docket
                  {purchaseOrder.requiresDeliveryDocket && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-14465">
                      Required
                    </span>}
                </h3>
              </div>
            </div>
            <div className="px-4 py-5 sm:p-6" data-id="element-14466">
              {purchaseOrder.requiresDeliveryDocket && <div className="mb-4" data-id="element-14467">
                  <div className="flex items-center" data-id="element-14468">
                    <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-14469" />
                    <p className="text-sm text-red-600" data-id="element-14470">
                      Your organisation requires this Good Receipt to have a
                      delivery docket. Please upload or take a photo of the
                      delivery docket.
                    </p>
                  </div>
                  {errors['deliveryDocket'] && <p className="mt-1 text-sm text-red-600" data-id="element-14471">
                      {errors['deliveryDocket']}
                    </p>}
                </div>}
              <div className="flex flex-wrap gap-3 mb-4" data-id="element-14472">
                <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14473">
                  <UploadIcon className="h-4 w-4 mr-2" data-id="element-14474" />
                  Upload Docket
                </button>
                {hasCamera && <button type="button" onClick={handleStartCamera} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14475">
                    <CameraIcon className="h-4 w-4 mr-2" data-id="element-14476" />
                    Take Photo
                  </button>}
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*,.pdf" className="hidden" multiple data-id="element-14477" />
              </div>
              {deliveryDockets.length > 0 ? <div data-id="element-14478">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-14479">
                    Uploaded Delivery Dockets ({deliveryDockets.length})
                  </h4>
                  <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-14480">
                    <ul className="divide-y divide-gray-200" data-id="element-14481">
                      {deliveryDockets.map(docket => <li key={docket.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50" data-id="element-14482">
                          <div className="flex items-center" data-id="element-14483">
                            {getFileIcon(docket.type)}
                            <div className="ml-3" data-id="element-14484">
                              <p className="text-sm font-medium text-gray-900" data-id="element-14485">
                                {docket.name}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-14486">
                                {formatFileSize(docket.size)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2" data-id="element-14487">
                            <a href={docket.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900 text-sm" data-id="element-14488">
                              View
                            </a>
                            <button onClick={() => handleRemoveDocket(docket.id)} className="text-red-600 hover:text-red-900" data-id="element-14489">
                              <XIcon className="h-4 w-4" data-id="element-14490" />
                            </button>
                          </div>
                        </li>)}
                    </ul>
                  </div>
                </div> : <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-md" data-id="element-14491">
                  <PaperclipIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-14492" />
                  <p className="mt-1 text-sm text-gray-500" data-id="element-14493">
                    No delivery docket uploaded yet
                  </p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-14494">
                    Upload or take a photo of the delivery docket
                  </p>
                </div>}
            </div>
          </div>
          {/* Camera Modal */}
          {showCameraModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-14495">
              <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-14496">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-14497">
                  <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-14498"></div>
                </div>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-14499">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-14500">
                    <div className="sm:flex sm:items-start" data-id="element-14501">
                      <div className="mt-3 text-center sm:mt-0 sm:text-left w-full" data-id="element-14502">
                        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14503">
                          Take Photo of Delivery Docket
                        </h3>
                        <div className="mt-4" data-id="element-14504">
                          <div className="relative" data-id="element-14505">
                            <video ref={videoRef} autoPlay playsInline className="w-full h-auto border border-gray-300 rounded-md" data-id="element-14506" />
                            <canvas ref={canvasRef} className="hidden" data-id="element-14507" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-14508">
                    <button type="button" onClick={handleCaptureImage} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-14509">
                      <CameraIcon className="h-4 w-4 mr-2" data-id="element-14510" />
                      Capture
                    </button>
                    <button type="button" onClick={handleCloseCamera} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-14511">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>}
          {/* Summary */}
          <ReceiptSummary totalThisReceipt={calculateTotals().totalThisReceipt} totalReceived={calculateTotals().totalReceived} totalRemaining={calculateTotals().totalRemaining} poTotal={purchaseOrder.total} currency={purchaseOrder.currency} locale={locale} data-id="element-14512" />
          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 sticky bottom-0 bg-white p-4 border-t border-gray-200 shadow-lg" data-id="element-14513">
            <button type="button" onClick={onBack} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14514">
              <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-14515" />
              Cancel
            </button>
            <button type="button" onClick={handleSaveAsDraft} disabled={!hasEnteredData()} className={`inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${hasEnteredData() ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50' : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'}`} data-id="element-14516">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-14517" />
              Save as Draft
            </button>
            <button type="button" onClick={handleSubmit} disabled={!isFormValid()} className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isFormValid() ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'}`} data-id="element-14518">
              <ClipboardCheckIcon className="h-4 w-4 mr-2" data-id="element-14519" />
              Submit Receipt
            </button>
          </div>
        </> : <div className="bg-white shadow rounded-lg p-6" data-id="element-14520">
          <div className="text-center" data-id="element-14521">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-14522">
              No Purchase Order Selected
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-14523">
              Please search for and select a purchase order to create a receipt.
            </p>
          </div>
        </div>}
    </div>;
}